<template>
	<Drawer
		:title="editableModel?.createdAt ? $t('Edit lesson plan') : $t('Add lesson plan')"
		initialWidth="3xl"
		:beforeClose="canClose"
		:closeOnClickOutside="true"
		:skipOpenAnimation="skipOpenAnimation"
		@requestUnmount="hide"
	>
		<template #default="{ hide }">
			<div style="display: none">{{ setSlotHide(hide) }}</div>

			<div v-if="!editableModel" class="flex items-center justify-center h-64">
				<span class="loading loading-spinner loading-lg"></span>
			</div>
			<form v-else @submit.prevent="save" class="h-full">
				<div class="px-6 pt-4 h-full" :class="[editableModel?.isSavedInLib && context === 'agenda' ? 'pb-20' : 'pb-4']">
					<div
						v-if="editableModel?.isSavedInLib && context === 'agenda'"
						class="flex items-start gap-3 p-2 mb-4 bg-red-200 text-error-content rounded"
					>
						<ExclamationTriangleIcon class="w-6 h-6 shrink-0" />
						<p>{{ $t("Careful: You are about to make changes to a lesson plan from your library.") }}</p>
					</div>
					<LessonPlanForm
						:lessonPlan="editableModel"
						:showOptionGuided="false"
						:errors="validationError"
						:suggestedGoals="suggestedGoals"
						:goalSourceContext="context === 'agenda' ? 'agendaLessonPlan' : 'lessonPlan'"
						:goalScope="context === 'agenda' ? 'school' : 'global'"
					/>
				</div>

				<div class="flex justify-end gap-1 absolute bottom-0 w-full bg-white/50 backdrop-blur-sm z-10 p-4 mt-6 border-t border-base-200">
					<div class="flex justify-end gap-1">
						<button
							type="button"
							class="btn btn-ghost"
							@click="cancel"
							:class="[saving || deleting ? 'hidden' : '']"
						>
							{{ $t("Cancel") }}
						</button>
						<button type="submit" class="btn" :class="[deleting ? 'btn-disabled' : 'btn-primary']">
							<span v-show="saving" class="loading loading-spinner"></span>
							{{ primaryButtonLabel }}
						</button>
					</div>
				</div>
			</form>
		</template>
	</Drawer>
</template>

<script lang="ts">
import { mapStores } from "pinia";

import { useLessonPlansStore } from "@/stores/LessonPlans.store";
import { useLessonPlanCollectionsStore } from "@/stores/LessonPlanCollections.store";
import { LessonPlan } from "@/models/LessonPlan.model";
import { LessonPlanCollection } from "@/models/LessonPlanCollection.model";

import Drawer from "@/components/ui/Drawer.v2.vue";
import LessonPlanForm from "@/components/LessonPlanForm.vue";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import { UnsavedChanges } from "@/utils/UnsavedChanges.util";
import {ApiErrorMessageBag} from "@/stores/errors/ApiErrorMessageBag.ts";

export default {
	emits: ["requestClose", "cancel", "saved"],

	props: {
		lessonPlan: {
			type: Object as () => LessonPlan | null,
			default: null,
		},
		selectedCollection: {
			type: Object as () => LessonPlanCollection | null,
			default: null,
		},
		context: {
			type: String,
			default: 'library',
		},
		skipOpenAnimation: {
			type: Boolean,
			default: false,
		},
		suggestedGoals: {
			type: Array,
			default: () => [],
		},
	},

	components: {
		Drawer,
		LessonPlanForm,
		ExclamationTriangleIcon,
	},

	data() {
		return {
			saving: false,
			deleting: false,
			originalModel: null as LessonPlan | null,
			editableModel: null as LessonPlan | null,
			validationError: null as any,
			slotHide: null as any,
			primaryButtonLabel: '' as string,
		};
	},
	computed: {
		...mapStores(useLessonPlansStore, useLessonPlanCollectionsStore),

		inUpdateMode() {
			return this.originalModel && this.originalModel.id ? true : false;
		},
	},

	methods: {
		setSlotHide(hideFn: any) {
			if (!this.slotHide) this.slotHide = hideFn;
			return;
		},

		hide() {
			this.lessonPlansStore.clearErrors();
			this.$emit("requestClose");
		},

		cancel() {
			if (this.inUpdateMode && this.originalModel) {
				// Go back to viewer — instant swap, no animation
				UnsavedChanges.unobserveModel();
				this.$emit('cancel', this.originalModel);
			} else {
				// Creating new plan — normal close with animation
				this.animatedClose();
			}
		},

		animatedClose() {
			UnsavedChanges.unobserveModel();
			this.slotHide ? this.slotHide() : this.hide();
		},

		canClose() {
			return UnsavedChanges.nonexistentOrIgnorable();
		},

		async save() {
			// Was the lesson plan changed?
			if (this.originalModel && this.editableModel && this.originalModel.equals(this.editableModel)) {
				this.animatedClose();
				return;
			}

			if (this.saving) {
				return;
			}
			this.saving = true;

			// Clear previous validation errors
			this.validationError = null;

			try {
                if (this.editableModel) {
                    // Validate before saving
                    this.editableModel.validate();

                    // Only persist to the API/library if we're NOT creating a new plan from the agenda context.
                    // In the agenda context, the plan is just attached to the lesson item locally
                    // and only saved to the library when the user explicitly requests it.
                    if (this.editableModel.isSavedInLib) {
                        await this.lessonPlansStore.save(this.editableModel);

                        // Add to selected collection if provided (for new plans created from a collection)
                        const collectionId = this.selectedCollection?.id ? String(this.selectedCollection.id).trim() : '';
                        const planId = this.editableModel.id ? String(this.editableModel.id).trim() : '';
                        if (collectionId && planId) {
                            try {
                                await this.lessonPlanCollectionsStore.addLessonPlanToCollection(this.selectedCollection, this.editableModel);
                                if (!this.editableModel.collectionIds) this.editableModel.collectionIds = [];
                                if (!this.editableModel.collectionIds.includes(collectionId)) {
                                    this.editableModel.collectionIds.push(collectionId);
                                }
                            } catch (e: any) {
                                console.warn('Failed to add lesson plan to collection:', e);
                            }
                        }
                    }

                    // Notify parent of the saved lesson plan
					const creatOrUpdate = this.inUpdateMode ? 'update' : 'create';
                    this.$emit('saved', this.editableModel, creatOrUpdate);
                }

                this.saving = false;
                this.animatedClose();
			} catch (e: any) {
				this.saving = false;

				// Check if it's a validation error
				if (e instanceof ApiErrorMessageBag && e.fields) {
					this.validationError = e;

					// Show toast for missing title
					if (e.fields.title) {
						this.$notify({
						text: this.$t('Without the required title, we cannot save your lesson plan.'),
							type: 'error',
						});
					}
				} else if (e instanceof ApiErrorMessageBag) {
					console.error("Error saving lesson plan:", e.toString());
				} else {
					throw e;
				}
			}
		},
	},

	async beforeMount() {
		// Check if we're editing an existing lesson plan (passed as prop)
		if (this.lessonPlan && this.lessonPlan.id) {
			// existing saved plan
			this.originalModel = this.lessonPlan;
			// clone the store model to allow for manipulation without instantly affecting the store
			this.editableModel = this.lessonPlan.clone();
			this.primaryButtonLabel = this.$t('Save changes');
			
			UnsavedChanges.observeModel(this.originalModel, this.editableModel);
		} else if (this.lessonPlan) {
			// unsaved plan with existing content
			this.editableModel = this.lessonPlan.clone();
			this.primaryButtonLabel = this.$t('Done');
			UnsavedChanges.observeModel(this.lessonPlan, this.editableModel);
		} else {
			// Creating a new lesson plan
			this.editableModel = await this.lessonPlansStore.new(true);
			this.editableModel.isGuided = true;
			this.primaryButtonLabel = this.$t('Add');

			UnsavedChanges.observeModel(this.editableModel.clone(), this.editableModel);
		}
	},

	async beforeUnmount() {
		UnsavedChanges.unobserveModel();
	},
};
</script>
